export interface ParsedExplanation {
    definition: string;
    synonyms: string[];
    antonyms: string[];
    collocations: string[];
    freeText: string;
}

const HEADER_RE = /^\[(Synonyms|Antonyms|Collocations|Definition)\]:\s*(.*)$/i;

export function parseList(value: string): string[] {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
}

export function parseExplanation(raw: string | undefined | null): ParsedExplanation {
    const result: ParsedExplanation = { definition: '', synonyms: [], antonyms: [], collocations: [], freeText: raw || '' };
    if (!raw) return result;

    const lines = raw.split('\n');
    let i = 0;
    let matchedAny = false;
    while (i < lines.length) {
        const match = lines[i].match(HEADER_RE);
        if (!match) break;
        matchedAny = true;
        const category = match[1].toLowerCase();
        if (category === 'definition') {
            // Prose, not a comma-separated list — keep as-is (just trimmed).
            result.definition = match[2].trim();
        } else {
            const values = parseList(match[2]);
            if (category === 'synonyms') result.synonyms = values;
            else if (category === 'antonyms') result.antonyms = values;
            else if (category === 'collocations') result.collocations = values;
        }
        i++;
    }

    if (!matchedAny) {
        result.freeText = raw;
        return result;
    }

    if (i < lines.length && lines[i].trim() === '') {
        i++;
    }
    result.freeText = lines.slice(i).join('\n');
    return result;
}

export function buildExplanationDisplay(parts: {
    freeText: string;
    definition?: string;
    synonyms: string[];
    antonyms: string[];
    collocations: string[];
}): string {
    const headerLines: string[] = [];
    if (parts.definition) headerLines.push(`[Definition]: ${parts.definition}`);
    if (parts.synonyms.length > 0) headerLines.push(`[Synonyms]: ${parts.synonyms.join(', ')}`);
    if (parts.antonyms.length > 0) headerLines.push(`[Antonyms]: ${parts.antonyms.join(', ')}`);
    if (parts.collocations.length > 0) headerLines.push(`[Collocations]: ${parts.collocations.join(', ')}`);

    if (headerLines.length === 0) return parts.freeText;
    return `${headerLines.join('\n')}\n\n${parts.freeText}`;
}

export function buildSavedExplanation(parts: {
    freeText: string;
    definition?: string;
    antonyms: string[];
    collocations: string[];
}): string {
    // Unlike synonyms (its own real DB column, deliberately excluded here), there is no
    // dedicated `definition` column — it belongs packed into the saved blob the same way
    // antonyms/collocations already are.
    return buildExplanationDisplay({ ...parts, synonyms: [] });
}

export function stripExplanationMetadata(raw: string | undefined | null): string {
    return parseExplanation(raw).freeText;
}

const HEADER_LABEL_RE = /^\[(Synonyms|Antonyms|Collocations|Definition)\]:/i;

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Renders plain-text `explanation` (as produced by buildExplanationDisplay) into HTML for
 * display in a contentEditable field, bolding the `[Synonyms]:`/`[Antonyms]:`/`[Collocations]:`
 * label on each header line. Requires a DOM (browser or jsdom) environment.
 */
export function explanationToHtml(text: string): string {
    return text
        .split('\n')
        .map((line) => {
            const match = line.match(HEADER_LABEL_RE);
            if (!match) return escapeHtml(line);
            const label = match[0];
            const rest = line.slice(label.length);
            return `<strong>${escapeHtml(label)}</strong>${escapeHtml(rest)}`;
        })
        .join('<br>');
}

/**
 * Inverse of `explanationToHtml`: converts a contentEditable field's `innerHTML` back into
 * plain text, unwrapping `<strong>`/`<b>` tags and normalizing block-level line breaks
 * (`<br>`, `<div>`, `<p>`) into `\n`. Requires a DOM (browser or jsdom) environment.
 */
export function explanationFromHtml(html: string): string {
    // An inert <template>: parsing into a plain div would load images and fire
    // their onerror handlers even though the div is never attached.
    const template = document.createElement('template');
    template.innerHTML = html;
    const container = template.content;
    let result = '';

    const walk = (node: ChildNode) => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent || '';
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        if (tag === 'br') {
            result += '\n';
            return;
        }
        const isBlock = tag === 'div' || tag === 'p';
        if (isBlock && result.length > 0 && !result.endsWith('\n')) result += '\n';
        el.childNodes.forEach(walk);
        if (isBlock && !result.endsWith('\n')) result += '\n';
    };

    container.childNodes.forEach(walk);
    return result.replace(/\n+$/, '');
}
